import ApiService from "./ApiService";

const WHPayoutService = {
  /**
   * Fetch WH payout report data with optional filters.
   *
   * @param {string} queryString - Encoded query string (property_slug, start_date, end_date, wh_payout_status, page)
   * @returns {Promise}
   */
  getWHPayoutReport(queryString) {
    let endPoint = "wh-payout" + (queryString !== "" ? "?" + queryString : "");
    let addditionalHeaderData = {};
    let postData = {};
    let inputData = {
      endPoint: endPoint,
      addditionalHeaderData: addditionalHeaderData,
      postData: postData,
    };
    return ApiService.getCall(inputData);
  },

  /**
   * Update the paid/unpaid status of a WH payout row.
   *
   * @param {Object} postData - { reservation_slug, wh_payout_status }
   * @returns {Promise}
   */
  updateWHPayoutStatus(postData) {
    let addditionalHeaderData = {};
    let inputData = {
      endPoint: "update-wh-payout-status",
      addditionalHeaderData: addditionalHeaderData,
      postData: postData,
    };
    return ApiService.postCall(inputData);
  },

  /**
   * Create a Wildhorn Stripe Express account and return the onboarding URL.
   *
   * @param {Object} postData
   * @returns {Promise}
   */
  createWHStripeExpress(postData) {
    let addditionalHeaderData = {};
    let inputData = {
      endPoint: "create-wh-stripe-express",
      addditionalHeaderData: addditionalHeaderData,
      postData: postData,
    };
    return ApiService.postCall(inputData);
  },

  /**
   * Delete / disconnect the Wildhorn Stripe Express account.
   *
   * @param {Object} postData
   * @returns {Promise}
   */
  deleteWHStripeAccount(postData) {
    let addditionalHeaderData = {};
    let inputData = {
      endPoint: "delete-wh-stripe-account",
      addditionalHeaderData: addditionalHeaderData,
      postData: postData,
    };
    return ApiService.postCall(inputData);
  },

  /**
   * Transfer unpaid WH FF&E Brokerage Fee earnings to the Wildhorn Stripe Express account.
   *
   * @param {Object} postData - Optional { property_slug, start_date, end_date }
   * @returns {Promise}
   */
  whStripeTransfer(postData) {
    let addditionalHeaderData = {};
    let inputData = {
      endPoint: "wh-stripe-transfer",
      addditionalHeaderData: addditionalHeaderData,
      postData: postData,
    };
    return ApiService.postCall(inputData);
  },

  /**
   * Update the paid/unpaid status of a WH Introductory Brokerage Fee payout row.
   * Tracked independently from updateWHPayoutStatus above.
   *
   * @param {Object} postData - { reservation_slug, intro_payout_status }
   * @returns {Promise}
   */
  updateIntroPayoutStatus(postData) {
    let addditionalHeaderData = {};
    let inputData = {
      endPoint: "update-intro-payout-status",
      addditionalHeaderData: addditionalHeaderData,
      postData: postData,
    };
    return ApiService.postCall(inputData);
  },

  /**
   * Transfer unpaid WH Introductory Brokerage Fee earnings to the Wildhorn
   * Stripe Express account. Separate from whStripeTransfer above.
   *
   * @param {Object} postData - Optional { property_slug, start_date, end_date }
   * @returns {Promise}
   */
  whIntroStripeTransfer(postData) {
    let addditionalHeaderData = {};
    let inputData = {
      endPoint: "wh-intro-stripe-transfer",
      addditionalHeaderData: addditionalHeaderData,
      postData: postData,
    };
    return ApiService.postCall(inputData);
  },

  /**
   * Set the payout cadence (monthly/quarterly/annually) for one of the
   * Wildhorn Creditor's Stripe transfers. FF&E Fee and Intro Fee cadences
   * are independent even though both use the same connected Stripe account.
   *
   * @param {Object} postData - { fee_type: "ffe"|"intro", cadence: "monthly"|"quarterly"|"annually" }
   * @returns {Promise}
   */
  updateWHStripeCadence(postData) {
    let addditionalHeaderData = {};
    let inputData = {
      endPoint: "update-wh-stripe-cadence",
      addditionalHeaderData: addditionalHeaderData,
      postData: postData,
    };
    return ApiService.postCall(inputData);
  },

  /**
   * Fetch the WH Revenue Share + FF&E consolidated report for all qualifying properties.
   * Returns unit-level rows for every unit with payout_wh > 0 or FF&E configured,
   * across ALL properties. Filter by property_slug to narrow to one property.
   *
   * @param {Object} addditionalHeaderData - Auth headers (triple-d spelling is intentional wire-contract).
   * @param {string} queryString           - Encoded query string (property_slug, start_date, end_date)
   * @returns {Promise}
   */
  getWHRevenueShareReport(addditionalHeaderData, queryString) {
    let endPoint =
      "wh-revenue-share-report" +
      (queryString !== "" ? "?" + queryString : "");
    let postData = {};
    let inputData = {
      endPoint: endPoint,
      addditionalHeaderData: addditionalHeaderData,
      postData: postData,
    };
    return ApiService.getCall(inputData);
  },

    getWHRevenueShareReport2(addditionalHeaderData, queryString) {
    let endPoint =
      "wh-revenue-share-report2" +
      (queryString !== "" ? "?" + queryString : "");
    let postData = {};
    let inputData = {
      endPoint: endPoint,
      addditionalHeaderData: addditionalHeaderData,
      postData: postData,
    };
    return ApiService.getCall(inputData);
  },

  /**
   * Export the WH Revenue Share Report to xlsx/csv. Hits the same endpoint
   * as getWHRevenueShareReport, but with is_export=1 so the backend returns
   * { filePath, fileName } instead of paginated rows.
   *
   * @param {string} queryString - Same filters as getWHRevenueShareReport, plus is_export=1&file_type=xlsx|csv
   * @returns {Promise}
   */
  exportWHRevenueShareReport(queryString) {
    let endPoint =
      "wh-revenue-share-report" +
      (queryString !== "" ? "?" + queryString : "");
    let addditionalHeaderData = {};
    let inputData = {
      endPoint: endPoint,
      addditionalHeaderData: addditionalHeaderData,
      postData: {},
    };
    return ApiService.getCall(inputData);
  },

    exportWHRevenueShareReport2(queryString) {
    let endPoint =
      "wh-revenue-share-report2" +
      (queryString !== "" ? "?" + queryString : "");
    let addditionalHeaderData = {};
    let inputData = {
      endPoint: endPoint,
      addditionalHeaderData: addditionalHeaderData,
      postData: {},
    };
    return ApiService.getCall(inputData);
  },

  /**
   * Fetch the editable column/group header labels for the WH Revenue Share
   * Report (default_label, custom_label, effective_label per column_key).
   *
   * @returns {Promise}
   */
  getWHRevenueShareReportLabels() {
    let addditionalHeaderData = {};
    let inputData = {
      endPoint: "wh-revenue-share-report-labels",
      addditionalHeaderData: addditionalHeaderData,
      postData: {},
    };
    return ApiService.getCall(inputData);
  },

  /**
   * Save edited column/group header labels for the Revenue Share Report.
   * Passing an empty string for a column resets it back to its default.
   *
   * @param {Object} postData - { labels: [{ column_key, custom_label }, ...] }
   * @returns {Promise}
   */
  updateWHRevenueShareReportLabels(postData) {
    let addditionalHeaderData = {};
    let inputData = {
      endPoint: "wh-revenue-share-report-labels",
      addditionalHeaderData: addditionalHeaderData,
      postData: postData,
    };
    return ApiService.postCall(inputData);
  },

  /**
   * Manually trigger a one-off WH Payout Report email for an arbitrary date
   * range, sent to the same recipients as the automated cadence-based
   * report. Independent of that cadence schedule — see
   * PayOutReport::sendManualWhPayoutReport() on the backend.
   *
   * @param {Object} postData - { start_date, end_date } (both required, "YYYY-MM-DD")
   * @returns {Promise}
   */
  sendManualWhPayoutReport(postData) {
    let addditionalHeaderData = {};
    let inputData = {
      endPoint: "wh-payout-report-send",
      addditionalHeaderData: addditionalHeaderData,
      postData: postData,
    };
    return ApiService.postCall(inputData);
  },

  /**
   * Fetch the Merriman-specific unit-level WH revenue share + FF&E report (legacy).
   *
   * @param {Object} addditionalHeaderData - Additional request headers (triple-d spelling is intentional wire-contract).
   * @param {string} queryString           - Encoded query string (start_date, end_date, property_slug)
   * @returns {Promise}
   */
  getMerrimanRevenueShareReport(addditionalHeaderData, queryString) {
    let endPoint =
      "merriman-revenue-share-report" +
      (queryString !== "" ? "?" + queryString : "");
    let postData = {};
    let inputData = {
      endPoint: endPoint,
      addditionalHeaderData: addditionalHeaderData,
      postData: postData,
    };
    return ApiService.getCall(inputData);
  },
};

export default WHPayoutService;
